import type {
  ThunkAction,
  EnhancedStore,
  UnknownAction,
} from '@reduxjs/toolkit';
import type {
  BusinessServiceConfig,
  Strategy,
  ResponseWrapper,
} from './abstractBusinessService.types';
import type { Logger } from 'loglevel';

/**
 * Abstract class representing a business service.
 * @deprecated Export RTKQueries directly without the abstraction layer.
 *             See waiting-room example.
 * @property {string} serviceName - The name of the service.
 * @property {EnhancedStore} store - The instance of the redux store to use in the service.
 * @property {Logger} logger - The instance of logger to use in the service.
 * @class
 */
export abstract class AbstractBusinessService<
  MethodName extends string,
  Store,
> {
  serviceName: string;

  store: Store & EnhancedStore;

  logger: Logger;

  strategyMap: Record<string, Strategy> = {};

  constructor({
    serviceName,
    store,
    logger,
    strategyMap,
  }: BusinessServiceConfig<Store>) {
    this.serviceName = serviceName;
    this.store = store;
    this.logger = logger;
    this.strategyMap = strategyMap;

    if (typeof window !== 'undefined') {
      this.logger.log('BusinessService registered:', this.serviceName);
    }
  }

  /**
   * Dispatch an action to the redux store and return a response of type T wrapped in a ResponseWrapper
   *
   * @template T
   * @param {ThunkAction} action - The action to dispatch.
   * @returns {Promise<ResponseWrapper<T>>} - The response from the action.
   */
  dispatchAction = async <T>(
    action: ThunkAction<unknown, unknown, unknown, UnknownAction>,
  ): Promise<ResponseWrapper<T>> => {
    return this.store.dispatch(
      action as unknown as UnknownAction,
    ) as unknown as ResponseWrapper<T>;
  };

  /**
   * Get the strategy for a specific method. Strategy is usually `OG` or `V2`
   * Each method can only use one strategy with the current implementation
   *
   * @param {string} methodName - The name of the method.
   * @returns {Strategy} - The strategy for the method.
   * @throws {TypeError} - Throws error if no strategy found for the method.
   */
  getStrategy = (methodName: MethodName): Strategy => {
    const foundStrategy = this.strategyMap[methodName];
    if (typeof foundStrategy !== 'string') {
      const errorMessage = `No strategy found for method: ${this.serviceName}.${methodName}`;
      this.logger.error(errorMessage);
      throw new TypeError(errorMessage);
    }
    return foundStrategy;
  };
}
